import { formatDistanceToNow } from 'date-fns';
import { ChartContainer } from '../../analytics/shared/ChartContainer';
import { EmptyState } from '../../analytics/shared/EmptyState';
import { ErrorState } from '../../analytics/shared/ErrorState';
import { PaginationControls } from '../../PaginationControls';
import { useDormantProvidersData } from '../../../hooks/useDormantProvidersData';
import type { EngagementMetricsQueryParams } from '../../../types/engagement-metrics.types';

type ProvidersNeedingAttentionTableProps = {
  queryParams: EngagementMetricsQueryParams;
};

/**
 * Days-idle severity tiers aren't specified numerically in engagement_metrics_design.md §1 —
 * only the status-pill color pairs are. ponytail: arbitrary 30/60-day split onto the
 * warning/error pairs; revisit if product wants a named threshold (e.g. 60/90-day tiers).
 */
const severityForDaysIdle = (daysIdle: number | null): { text: string; bg: string; label: string } => {
  if (daysIdle === null) return { text: '#7f8a83', bg: '#f0ede6', label: 'Never engaged' };
  if (daysIdle < 60) return { text: '#8a6d1f', bg: '#f3edd9', label: `${daysIdle}d idle` };
  return { text: '#a83a2a', bg: '#f5e3df', label: `${daysIdle}d idle` };
};

const DaysIdleBadge = ({ daysIdle }: { daysIdle: number | null }) => {
  const severity = severityForDaysIdle(daysIdle);
  return (
    <span
      className="inline-flex items-center justify-center whitespace-nowrap rounded-full px-2.5 py-1 text-[11px] font-bold leading-none"
      style={{ color: severity.text, backgroundColor: severity.bg }}
    >
      {severity.label}
    </span>
  );
};

const LastEngagementCell = ({ lastEngagementAt }: { lastEngagementAt: string | null }) => {
  if (!lastEngagementAt) {
    return <span className="text-[12.5px]" style={{ color: '#7f8a83' }}>—</span>;
  }
  const date = new Date(lastEngagementAt);
  return (
    <div>
      <p className="text-[12.5px] font-semibold" style={{ color: '#1b2a23' }}>
        {formatDistanceToNow(date, { addSuffix: true })}
      </p>
      <p className="text-[11px]" style={{ color: '#7f8a83' }}>
        {date.toLocaleDateString()}
      </p>
    </div>
  );
};

export const ProvidersNeedingAttentionTable = ({
  queryParams,
}: ProvidersNeedingAttentionTableProps) => {
  const {
    items,
    meta,
    filterOptions,
    isLoading,
    isError,
    refetch,
    setPage,
    setPageSize,
    categoryName,
    setCategoryName,
    specialtyName,
    setSpecialtyName,
  } = useDormantProvidersData(queryParams);

  return (
    <ChartContainer
      title="Providers Needing Attention"
      subtitle="Approved providers with no engagement in 30+ days"
    >
      <div className="mb-4 flex flex-wrap items-center gap-3">
        <label className="flex items-center gap-2 text-[12.5px]" style={{ color: '#46584f' }}>
          Category
          <select
            aria-label="Category"
            value={categoryName}
            onChange={(e) => setCategoryName(e.target.value)}
            className="rounded-md border px-2 py-1 text-[12.5px]"
            style={{ borderColor: '#e7e2d8' }}
          >
            <option value="">All categories</option>
            {filterOptions.categoryNames.map((name) => (
              <option key={name} value={name}>
                {name}
              </option>
            ))}
          </select>
        </label>

        <label className="flex items-center gap-2 text-[12.5px]" style={{ color: '#46584f' }}>
          Specialty
          <select
            aria-label="Specialty"
            value={specialtyName}
            onChange={(e) => setSpecialtyName(e.target.value)}
            className="rounded-md border px-2 py-1 text-[12.5px]"
            style={{ borderColor: '#e7e2d8' }}
          >
            <option value="">All specialties</option>
            {filterOptions.specialtyNames.map((name) => (
              <option key={name} value={name}>
                {name}
              </option>
            ))}
          </select>
        </label>
      </div>

      {isError ? (
        <ErrorState
          message="Failed to load providers needing attention. Please try again."
          onRetry={() => refetch()}
        />
      ) : !isLoading && items.length === 0 ? (
        <EmptyState
          message={
            categoryName || specialtyName
              ? 'No dormant providers match the selected filters.'
              : 'No providers are currently dormant.'
          }
        />
      ) : (
        <>
          <div className="overflow-x-auto">
            <table className="w-full min-w-[560px]">
              <thead>
                <tr className="border-b" style={{ borderColor: '#edeae3' }}>
                  {['Provider', 'Category / Specialty', 'Last Engagement', 'Days Idle'].map(
                    (header) => (
                      <th
                        key={header}
                        className="px-3 py-2 text-left text-[11px] font-bold uppercase tracking-[0.3px]"
                        style={{ color: '#7f8a83' }}
                      >
                        {header}
                      </th>
                    ),
                  )}
                </tr>
              </thead>
              <tbody>
                {items.map((provider) => (
                  <tr
                    key={provider.providerUserId}
                    className="border-b last:border-0"
                    style={{ borderColor: '#edeae3' }}
                  >
                    <td className="px-3 py-2.5">
                      <p className="text-[13px] font-semibold" style={{ color: '#1b2a23' }}>
                        {provider.providerName}
                      </p>
                      <p className="text-[11px]" style={{ color: '#7f8a83' }}>
                        {provider.companyName ?? 'Independent provider'}
                      </p>
                    </td>
                    <td className="px-3 py-2.5 text-[12.5px]" style={{ color: '#46584f' }}>
                      {[...provider.categoryNames, ...provider.specialtyNames].join(', ') || '—'}
                    </td>
                    <td className="px-3 py-2.5">
                      <LastEngagementCell lastEngagementAt={provider.lastEngagementAt} />
                    </td>
                    <td className="px-3 py-2.5">
                      <DaysIdleBadge daysIdle={provider.daysIdle} />
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {meta && (
            <PaginationControls
              meta={meta}
              onPageChange={setPage}
              onPageSizeChange={setPageSize}
              pageSizeOptions={[10, 25, 50, 100]}
            />
          )}
        </>
      )}
    </ChartContainer>
  );
};
